import { API_CONFIG } from '@Web/config/api.config';
import { RestClient } from '@Web/clients/RestClient';
import { AuthService } from '@Web/services/AuthService';
import { formatCentsToCurrency } from '@core/utils/CurrencyFormatter';
import {
  computeMaxWinPerBet,
  type GameBetLimits,
} from '@core/gameengine/config/GameBetLimits';

type RawConfig = Record<string, unknown>;

function toNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Crash REST APIs send monetary amounts in the smallest currency unit (cents). */
function toCurrencyAmount(value: unknown): number | null {
  const n = toNumber(value);
  if (n == null) return null;
  return formatCentsToCurrency(n);
}

function pickAmount(source: RawConfig, keys: string[]): number | null {
  for (const key of keys) {
    const amount = toCurrencyAmount(source[key]);
    if (amount != null) return amount;
  }
  return null;
}

/** Crash APIs may send multipliers as fixed-point (e.g. 1000000 => 10x). */
function toPayoutMultiplier(value: unknown): number | null {
  const n = toNumber(value);
  if (n == null || n <= 0) return null;

  // Crash tick encoding (261500 => 2.615x, 1000000 => 10x)
  if (Number.isInteger(n) && n >= 100_000) {
    return n / 100_000;
  }

  // Config fixed-point ×100 (1000 => 10.00x)
  if (Number.isInteger(n) && n >= 100 && n < 100_000) {
    const scaled = n / 100;
    if (scaled >= 1 && scaled <= 10_000) {
      return scaled;
    }
  }

  return n;
}

/** Only fields that represent the max payout multiplier for win-cap (not auto-cashout max). */
function pickMaxPayoutMultiplier(source: RawConfig): number | null {
  const keys = [
    'maxPayoutMultiplier',
    'max_payout_multiplier',
    'maximumPayoutMultiplier',
    'maximum_payout_multiplier',
  ];

  for (const key of keys) {
    const multiplier = toPayoutMultiplier(source[key]);
    if (multiplier != null) return multiplier;
  }

  return null;
}

function flattenConfigPayload(data: RawConfig): RawConfig {
  const nested =
    data.betLimits ??
    data.limits ??
    data.config ??
    data.data ??
    data.payload ??
    data.gameConfig;

  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return { ...data, ...(nested as RawConfig) };
  }

  return data;
}

export function mapCrashConfigToBetLimits(
  data: RawConfig
): Partial<GameBetLimits> | null {
  const source = flattenConfigPayload(data);
  const denominations: unknown[] = Array.isArray(source.coinDenominations)
    ? source.coinDenominations
    : [];
  const stakeValues = denominations
    .filter(value => typeof value === 'string' || typeof value === 'number')
    .map(value => Number(value) / 10)
    .filter(value => Number.isFinite(value) && value > 0)
    .sort((a, b) => a - b);
  const minBet = pickAmount(source, [
    'minBet',
    'min_bet',
    'minBetAmount',
    'min_bet_amount',
    'minimumBet',
    'minimum_bet',
  ]);
  const maxBet = pickAmount(source, [
    'maxBet',
    'max_bet',
    'maxBetAmount',
    'max_bet_amount',
    'maximumBet',
    'maximum_bet',
  ]);
  let maxPayoutMultiplier = pickMaxPayoutMultiplier(source);
  const maxWinFromApi = pickAmount(source, [
    'maxWinPerBet',
    'max_win_per_bet',
    'maxWinAmount',
    'max_win_amount',
    'maximumWinPerBet',
    'maximum_win_per_bet',
  ]);

  // Derive payout multiplier when API only sends the win cap amount.
  if (
    maxPayoutMultiplier == null &&
    maxBet != null &&
    maxBet > 0 &&
    maxWinFromApi != null
  ) {
    maxPayoutMultiplier = maxWinFromApi / maxBet;
  }

  if (
    minBet == null &&
    maxBet == null &&
    maxPayoutMultiplier == null &&
    !stakeValues.length
  ) {
    return null;
  }

  const partial: Partial<GameBetLimits> = {
    ...(stakeValues.length ? { stakeValues } : {}),
    ...(minBet != null ? { minBet } : {}),
    ...(maxBet != null ? { maxBet } : {}),
    ...(maxPayoutMultiplier != null ? { maxPayoutMultiplier } : {}),
  };

  if (partial.maxBet != null && partial.maxPayoutMultiplier != null) {
    partial.maxWinPerBet = computeMaxWinPerBet(
      partial.maxBet,
      partial.maxPayoutMultiplier
    );
  }

  return partial;
}

export class CrashConfigService {
  private static instance: CrashConfigService;
  private restClient: RestClient;

  private constructor() {
    this.restClient = new RestClient(API_CONFIG.baseUrl);
  }

  public static getInstance(): CrashConfigService {
    if (!CrashConfigService.instance) {
      CrashConfigService.instance = new CrashConfigService();
    }
    return CrashConfigService.instance;
  }

  private getHeaders() {
    const token = AuthService.getInstance().getAccessToken();
    return token
      ? {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      : { 'Content-Type': 'application/json' };
  }

  public async getBetLimits(
    gameId: number
  ): Promise<Partial<GameBetLimits> | null> {
    try {
      const url = API_CONFIG.endpoints.crashConfig(gameId);
      const data = await this.restClient.get<RawConfig>(url, {
        headers: this.getHeaders(),
      });
      return mapCrashConfigToBetLimits(data);
    } catch (error) {
      console.error(
        '[CrashConfigService] Failed to fetch crash config:',
        error
      );
      return null;
    }
  }
}
